<script setup>
import apiClient from "@/apiClient.js";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import dayjs from "dayjs";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  MinusCircleIcon,
  PaperAirplaneIcon,
  PlusCircleIcon,
} from "@heroicons/vue/24/solid";

const apiUrl = process.env.VUE_APP_API_URL;
const route = useRoute();
const router = useRouter();
const breadcrumbStore = useBreadcrumbStore();
const purchaseOrder = ref({});
const candidateInvoices = ref([]);
const selectedInvoiceIds = ref([]);
const loading = ref(false);
const saving = ref(false);
const paying = ref(false);
const emailSending = ref(false);
const errorMessage = ref("");
const emailError = ref("");
const emailSuccess = ref("");
const emailRecipientIds = ref([]);
const emailSubject = ref("");
const emailBody = ref("");
const pdfTimestamp = ref(Date.now());

const documentSrc = computed(() =>
  purchaseOrder.value?.id
    ? `${apiUrl}/api/purchase-orders/${purchaseOrder.value.id}/document?${pdfTimestamp.value}`
    : "",
);

const linkedInvoices = computed(() => purchaseOrder.value?.invoices ?? []);
const emailContacts = computed(() =>
  (purchaseOrder.value?.customer?.contacts ?? []).filter((contact) => contact?.email?.trim()),
);
const canMarkPaid = computed(() =>
  linkedInvoices.value.length > 0 && purchaseOrder.value?.payment_status_label !== "Paid",
);
const canSendEmail = computed(() =>
  linkedInvoices.value.length > 0
  && emailRecipientIds.value.length > 0
  && emailSubject.value.trim().length > 0
  && !emailSending.value,
);

const formatMoney = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(Number(value ?? 0) / 100);

const formatDate = (value) => {
  if (!value) {
    return "Not set";
  }
  return dayjs(value).isValid() ? dayjs(value).format("D MMM YYYY") : "Not set";
};

const formatDateTime = (value) => {
  if (!value || !dayjs(value).isValid()) {
    return "";
  }

  return dayjs(value).format("D MMM YYYY [at] HH:mm");
};

const invoiceLabel = (invoice) => {
  const reference = invoice?.reference?.txt ?? `Invoice #${invoice?.id}`;
  const name = invoice?.reference?.name ?? "Invoice";
  return `${reference} · ${name}`;
};

const invoiceTotal = (invoice) =>
  Number(invoice?.subtotal ?? 0) + Number(invoice?.vat ?? 0);

const refreshPdf = () => {
  pdfTimestamp.value = Date.now();
};

const loadCandidateInvoices = async () => {
  const customerId = purchaseOrder.value?.customer_id;
  if (!customerId) {
    candidateInvoices.value = [];
    return;
  }

  const { data } = await apiClient.get(`/api/customer/${customerId}`);
  candidateInvoices.value = (data?.invoices ?? [])
    .filter((invoice) => !invoice?.customer_purchase_order_id)
    .sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0));
};

const loadPurchaseOrder = async () => {
  loading.value = true;
  errorMessage.value = "";

  try {
    const { data } = await apiClient.get(`/api/purchase-orders/${route.params.id}`);
    purchaseOrder.value = data;
    if (!emailSubject.value) {
      emailSubject.value = data?.email_template?.subject ?? "Your consolidated invoice is ready";
    }
    if (!emailRecipientIds.value.length && Array.isArray(data?.sent_recipient_contact_ids)) {
      emailRecipientIds.value = data.sent_recipient_contact_ids.map((id) => Number(id));
    }
    breadcrumbStore.setValue(data?.po_number ?? "Customer PO Invoice");
    await loadCandidateInvoices();
    refreshPdf();
  } catch (error) {
    errorMessage.value = error?.response?.data?.message ?? "Unable to load customer PO invoice.";
  } finally {
    loading.value = false;
  }
};

const addSelectedInvoices = async () => {
  const invoiceIds = selectedInvoiceIds.value.map((id) => Number(id)).filter(Boolean);
  if (!invoiceIds.length || saving.value) {
    return;
  }

  saving.value = true;
  errorMessage.value = "";

  try {
    await apiClient.post(`/api/purchase-orders/${purchaseOrder.value.id}/invoices`, {
      invoice_ids: invoiceIds,
    });
    selectedInvoiceIds.value = [];
    await loadPurchaseOrder();
  } catch (error) {
    errorMessage.value = error?.response?.data?.message ?? "Unable to add invoices.";
  } finally {
    saving.value = false;
  }
};

const removeInvoice = async (invoice) => {
  if (saving.value) {
    return;
  }

  saving.value = true;
  errorMessage.value = "";

  try {
    await apiClient.delete(`/api/purchase-orders/${purchaseOrder.value.id}/invoices/${invoice.id}`);
    await loadPurchaseOrder();
  } catch (error) {
    errorMessage.value = error?.response?.data?.message ?? "Unable to remove invoice.";
  } finally {
    saving.value = false;
  }
};

const markPaid = async () => {
  if (!canMarkPaid.value || paying.value) {
    return;
  }

  paying.value = true;
  errorMessage.value = "";

  try {
    await apiClient.put(`/api/purchase-orders/${purchaseOrder.value.id}/paid`);
    await loadPurchaseOrder();
  } catch (error) {
    errorMessage.value = error?.response?.data?.message ?? "Unable to mark invoices as paid.";
  } finally {
    paying.value = false;
  }
};

const sendEmail = async () => {
  if (!canSendEmail.value) {
    return;
  }

  emailSending.value = true;
  emailError.value = "";
  emailSuccess.value = "";

  try {
    const { data } = await apiClient.post(
      `/api/purchase-orders/${purchaseOrder.value.id}/email`,
      {
        recipients: emailRecipientIds.value.map((id) => Number(id)),
        subject: emailSubject.value.trim(),
        body: emailBody.value.trim(),
      },
    );

    purchaseOrder.value = data?.purchase_order ?? purchaseOrder.value;
    emailSuccess.value = data?.message ?? "Consolidated invoice sent.";
  } catch (error) {
    emailError.value = error?.response?.data?.message
      ?? error?.response?.data?.errors?.recipients?.[0]
      ?? "Unable to send the consolidated invoice.";
  } finally {
    emailSending.value = false;
  }
};

const openDocument = () => {
  if (!purchaseOrder.value?.id) {
    return;
  }
  open(`${apiUrl}/api/purchase-orders/${purchaseOrder.value.id}/document`, "_blank");
};

onMounted(() => {
  loadPurchaseOrder();
});
</script>

<template>
  <div>
    <form-box :header="purchaseOrder.po_number || 'Customer PO Invoice'">
      <template #nav>
        <div class="flex flex-wrap justify-end gap-2">
          <button class="btn" type="button" @click.prevent="router.push('/invoice')">
            Back to invoices
          </button>
          <button class="btn" type="button" @click.prevent="openDocument">
            <DocumentArrowDownIcon class="h-4 w-4" />
            PDF
          </button>
          <button
            class="btn accent"
            type="button"
            :disabled="!canMarkPaid || paying"
            @click.prevent="markPaid"
          >
            <CheckCircleIcon class="h-4 w-4" />
            {{ purchaseOrder.payment_status_label === "Paid" ? "Paid" : "Mark group paid" }}
          </button>
        </div>
      </template>

      <div v-if="errorMessage" class="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ errorMessage }}
      </div>

      <div v-if="purchaseOrder.id" class="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Customer</div>
          <div class="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {{ purchaseOrder.customer?.company }}
          </div>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</div>
          <div class="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {{ purchaseOrder.payment_status_label || "Unpaid" }}
          </div>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoices</div>
          <div class="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {{ purchaseOrder.invoice_count ?? 0 }}
          </div>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total</div>
          <div class="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {{ formatMoney(purchaseOrder.total ?? 0) }}
          </div>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Unpaid</div>
          <div class="mt-1 text-sm font-semibold text-rose-600">
            {{ formatMoney(purchaseOrder.unpaid_total ?? 0) }}
          </div>
        </div>
      </div>
      <div v-else-if="loading" class="text-sm text-slate-500">Loading customer PO invoice...</div>
    </form-box>

    <div v-if="purchaseOrder.id" class="space-y-4">
      <form-box header="Invoices">
        <div class="space-y-5">
          <div>
            <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">Included invoices</h3>
                <p class="text-xs text-slate-500 dark:text-slate-400">These invoices are covered by this customer PO invoice.</p>
              </div>
              <span class="text-xs font-semibold text-slate-500">{{ linkedInvoices.length }} included</span>
            </div>
            <div v-if="linkedInvoices.length" class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
              <table class="w-full table-auto">
                <thead class="bg-slate-50 dark:bg-slate-900/40">
                  <tr class="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th class="px-4 py-3">Invoice</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3 text-right">Total</th>
                    <th class="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                  <tr v-for="invoice in linkedInvoices" :key="invoice.id">
                    <td class="px-4 py-3">
                      <router-link :to="`/invoice/${invoice.id}`" class="font-semibold text-slate-900 hover:underline dark:text-slate-100">
                        {{ invoiceLabel(invoice) }}
                      </router-link>
                    </td>
                    <td class="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {{ invoice.payment_status_label }}
                      <span v-if="invoice.paid_at" class="text-xs text-slate-400">
                        · {{ formatDate(invoice.paid_at) }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">
                      {{ formatMoney(invoiceTotal(invoice)) }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      <button
                        class="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                        type="button"
                        :disabled="saving || purchaseOrder.payment_status_label === 'Paid'"
                        @click.prevent="removeInvoice(invoice)"
                      >
                        <MinusCircleIcon class="h-4 w-4" />
                        Remove
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              No invoices are linked to this customer PO yet.
            </div>
          </div>

          <div class="h-px bg-slate-200 dark:bg-slate-700"></div>

          <div>
            <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">Add invoices</h3>
                <p class="text-xs text-slate-500 dark:text-slate-400">Searchable selection of this customer's unassigned invoices.</p>
              </div>
              <button
                class="btn accent inline-flex items-center gap-2 whitespace-nowrap"
                type="button"
                :disabled="saving || selectedInvoiceIds.length === 0"
                @click.prevent="addSelectedInvoices"
              >
                <PlusCircleIcon class="h-4 w-4 shrink-0" />
                Add selected
              </button>
            </div>
            <div v-if="candidateInvoices.length" class="max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <label v-for="invoice in candidateInvoices" :key="invoice.id"
                class="flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40">
                <input v-model="selectedInvoiceIds" type="checkbox" class="mt-1" :value="invoice.id" />
                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {{ invoiceLabel(invoice) }}
                  </div>
                  <div class="truncate text-xs text-slate-500">{{ formatDate(invoice.issue_date) }}</div>
                </div>
                <div class="shrink-0 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {{ formatMoney(invoiceTotal(invoice)) }}
                </div>
              </label>
            </div>
            <div v-else class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              No unassigned invoices are available for this customer.
            </div>
          </div>
        </div>
      </form-box>

      <form-box header="Send consolidated invoice">
        <template #nav>
          <span v-if="purchaseOrder.sent_at" class="text-xs text-slate-500 dark:text-slate-400">
            Last sent {{ formatDateTime(purchaseOrder.sent_at) }}
            <template v-if="purchaseOrder.sent_by?.name">by {{ purchaseOrder.sent_by.name }}</template>
          </span>
        </template>

        <div class="space-y-5">
          <div class="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200">
            One consolidated PDF covering {{ linkedInvoices.length }} linked
            {{ linkedInvoices.length === 1 ? "invoice" : "invoices" }} will be attached.
            Sending it also marks those invoices as sent in Neptune.
          </div>

          <div v-if="emailSuccess" class="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {{ emailSuccess }}
          </div>
          <div v-if="emailError" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {{ emailError }}
          </div>

          <div>
            <div class="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Recipients</div>
            <div v-if="emailContacts.length" class="grid gap-2 md:grid-cols-2">
              <label
                v-for="contact in emailContacts"
                :key="contact.id"
                class="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40"
              >
                <input v-model="emailRecipientIds" type="checkbox" class="mt-1" :value="contact.id" />
                <span class="min-w-0">
                  <span class="block truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {{ contact.name || "Customer contact" }}
                  </span>
                  <span class="block truncate text-xs text-slate-500">{{ contact.email }}</span>
                </span>
              </label>
            </div>
            <div v-else class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Add an email address to a customer contact before sending this invoice.
            </div>
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Email subject
            </label>
            <input
              v-model="emailSubject"
              type="text"
              class="block w-full"
              placeholder="Your consolidated invoice is ready"
            />
            <p class="mt-1 text-xs text-slate-500">
              Neptune will add [{{ purchaseOrder.po_number }}] to the subject.
            </p>
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Additional message (optional)
            </label>
            <textarea
              v-model="emailBody"
              class="block min-h-32 w-full"
              placeholder="Add any extra information for the customer."
            ></textarea>
          </div>

          <div class="flex justify-end">
            <button
              class="btn accent inline-flex items-center gap-2 whitespace-nowrap"
              type="button"
              :disabled="!canSendEmail"
              @click.prevent="sendEmail"
            >
              <PaperAirplaneIcon class="h-4 w-4 shrink-0" />
              {{ emailSending ? "Sending..." : (purchaseOrder.sent_at ? "Send again" : "Send invoice") }}
            </button>
          </div>
        </div>
      </form-box>

      <form-box header="Document">
        <template #nav>
          <button
            class="btn inline-flex items-center gap-2 whitespace-nowrap"
            type="button"
            @click.prevent="refreshPdf"
          >
            <ArrowPathIcon class="h-4 w-4 shrink-0" />
            Refresh
          </button>
        </template>
        <iframe
          v-if="documentSrc"
          :src="documentSrc"
          class="h-[72vh] w-full rounded-lg border border-slate-200 bg-white dark:border-slate-700"
        ></iframe>
      </form-box>
    </div>
  </div>
</template>
